"use client";

import { useState } from "react";
import {
  SOCIAL_FIELD_NAMES,
  SOCIAL_PLATFORM_NAMES,
  SOCIAL_PLATFORMS,
  type BusinessProfileRecord,
  type Category,
} from "@/modules/catalog/surfaces";
import { MapPicker } from "./MapPicker";
import { PhotosEditor, type PhotoItem } from "./PhotosEditor";
import { ActionSubmit, Banner, FIELD_INPUT, FIELD_LABEL, Field } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * BusinessProfileEditScreen (docs/ux/screens/BusinessProfileEditScreen.md),
 * shared by both frontends (R22 T14).
 *
 * The map picker is the design system's §12 trap in person: it cannot mount
 * hidden. It is rendered inline here rather than behind a disclosure for
 * exactly that reason, and moving this component must not change it.
 *
 * **The photos travel in the `FormData` as `photo` entries, in order** (H59):
 * an address for one already shown, the `File` for one picked here. The host
 * uploads the files and sends the addresses, which keeps this layer free of
 * any storage client, the same split the logo has always had.
 */

const ABOUT_MAX = 1000;

export function BusinessProfileForm({
  profile,
  categories,
  save,
}: {
  profile: BusinessProfileRecord;
  categories: Category[];
  save: FormAction;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>(() =>
    profile.photoUrls.map((url) => ({ kind: "existing", url })),
  );
  const [photoError, setPhotoError] = useState<string | null>(null);
  // A save re-reads the profile, and the grid must then hold the saved
  // addresses: the files it picked are uploaded now, and keeping them as
  // files would upload them again on the next save. Adjusted during render,
  // when the saved list changes, rather than in an effect.
  const savedPhotos = profile.photoUrls.join("\n");
  const [shownFor, setShownFor] = useState(savedPhotos);
  if (shownFor !== savedPhotos) {
    setShownFor(savedPhotos);
    setPhotos(profile.photoUrls.map((url) => ({ kind: "existing", url })));
  }
  const [about, setAbout] = useState(profile.about ?? "");
  const { state, pending, onSubmit } = useFormAction((form) => {
    form.delete("photo");
    for (const photo of photos) {
      form.append("photo", photo.kind === "existing" ? photo.url : photo.file);
    }
    return save(form);
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const socialLink = (platform: (typeof SOCIAL_PLATFORMS)[number]) =>
    profile.socialLinks.find((link) => link.platform === platform)?.url ?? "";
  const pinError = state.fieldErrors?.latitude ?? state.fieldErrors?.longitude;
  const currentLogo = logoPreview ?? profile.logoUrl;

  return (
    <>
      <h1 className="mb-1 text-xl font-bold text-ink">Edit profile</h1>
      <p className="mb-6 text-sm text-muted">
        Changes go live immediately, no re-approval needed
      </p>
      {state.formError && <Banner>{state.formError}</Banner>}
      {state.success && <Banner tone="success">{state.success}</Banner>}
      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <div className="mb-4">
            <span className={FIELD_LABEL}>Logo</span>
            <div className="flex items-center gap-4">
              {currentLogo ? (
                // Plain <img>: the preview is a local object URL (blob:),
                // which next/image cannot optimize.
                <img
                  src={currentLogo}
                  alt="Business logo"
                  className="h-14 w-14 shrink-0 rounded-xl border border-line object-cover"
                />
              ) : (
                <div
                  aria-hidden
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-brand text-xl font-bold text-on-brand"
                >
                  {profile.name.trim().charAt(0).toUpperCase() || "?"}
                </div>
              )}
              <div>
                <input
                  id="logo"
                  name="logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-invalid={!!state.fieldErrors?.logo}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setLogoPreview(file && file.size > 0 ? URL.createObjectURL(file) : null);
                  }}
                  className="block w-full text-sm text-body file:me-3 file:rounded-lg file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-on-accent"
                />
                <p className="mt-1 text-xs text-muted">PNG, JPEG or WebP, up to 2 MB.</p>
              </div>
            </div>
            {state.fieldErrors?.logo && (
              <p className="mt-1 text-sm text-danger">{state.fieldErrors.logo}</p>
            )}
          </div>

          <Field
            label="Business name"
            name="businessName"
            defaultValue={state.values?.businessName ?? profile.name}
            error={state.fieldErrors?.businessName}
          />

          {/* H59: required on every save, so a clinic approved without one
              writes it here the first time it edits its profile. */}
          <div className="mb-4">
            <label htmlFor="about" className={FIELD_LABEL}>
              About your clinic
            </label>
            <textarea
              id="about"
              name="about"
              rows={5}
              maxLength={ABOUT_MAX}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              aria-invalid={!!state.fieldErrors?.about}
              placeholder="What you treat, who you see, what to expect at a first visit."
              className={FIELD_INPUT}
            />
            <div className="mt-1 flex justify-between gap-3 text-xs">
              <span className="text-danger">{state.fieldErrors?.about}</span>
              <span className="shrink-0 text-muted">
                {about.length.toLocaleString("en-US")} / 1,000
              </span>
            </div>
          </div>

          <PhotosEditor
            photos={photos}
            onChange={setPhotos}
            error={photoError}
            onError={setPhotoError}
          />

          <div className="mb-4">
            <span className={FIELD_LABEL}>
              What do you offer?
            </span>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    name="categoryIds"
                    value={category.id}
                    defaultChecked={profile.categoryIds.includes(category.id)}
                    className="accent-accent"
                  />
                  {category.name}
                </label>
              ))}
            </div>
            {state.fieldErrors?.categoryIds && (
              <p className="mt-1 text-sm text-danger">{state.fieldErrors.categoryIds}</p>
            )}
          </div>

          <MapPicker
            error={pinError}
            initial={{ lat: profile.latitude, lng: profile.longitude }}
          />

          <Field
            label="Website (optional)"
            name="website"
            defaultValue={state.values?.website ?? profile.website ?? ""}
            error={state.fieldErrors?.website}
            placeholder="https://…"
          />

          {/* H59: optional, one per platform, each on its own domain. */}
          <fieldset className="mb-4">
            <legend className={FIELD_LABEL}>Social media (optional)</legend>
            {SOCIAL_PLATFORMS.map((platform) => (
              <Field
                key={platform}
                label={SOCIAL_PLATFORM_NAMES[platform]}
                name={SOCIAL_FIELD_NAMES[platform]}
                defaultValue={
                  state.values?.[SOCIAL_FIELD_NAMES[platform]] ?? socialLink(platform)
                }
                error={state.fieldErrors?.[SOCIAL_FIELD_NAMES[platform]]}
                placeholder="https://…"
              />
            ))}
          </fieldset>

          {/* Home-visit surcharge (H24): business-wide flat fee for At-Home
              bookings. Empty ⇒ $0 (no surcharge). */}
          <Field
            label="Home-visit surcharge (optional)"
            name="homeSurcharge"
            defaultValue={
              state.values?.homeSurcharge ??
              (profile.homeSurchargeCents > 0
                ? (profile.homeSurchargeCents / 100).toString()
                : "")
            }
            error={state.fieldErrors?.homeSurcharge}
            placeholder="0.00"
          />
          <p className="-mt-2 mb-4 text-xs text-muted">
            Added to any at-home booking, on top of the service price.
          </p>

          <ActionSubmit pendingLabel="Saving…" pending={pending}>
            Save changes
          </ActionSubmit>
        </fieldset>
      </form>
    </>
  );
}
